import type { Theme } from '@mui/material/styles';

/**
 * Semantic tone for status pills, delta chips, and ranked-bar tints. These are
 * the Polaris kit tones. `neutral`, `accent`, and `indigo` have no direct MUI
 * palette entry, so {@link resolveTone} maps them to slate / secondary / the
 * documented Polaris indigo ramp.
 */
export type StatusSeverity =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'
  | 'accent'
  | 'indigo';

/** The three shades a tone exposes (mirrors MUI's `PaletteColor`). */
export interface ToneColors {
  main: string;
  dark: string;
  light: string;
}

/**
 * Resolve a semantic {@link StatusSeverity} to concrete theme colors. Standard
 * severities read straight from the palette; `neutral` is Polaris slate and
 * `accent` is the secondary (orange) ramp. Never hard-codes a hex the theme
 * already owns — the two slate shades are the only literals, and they are the
 * documented Polaris neutral ramp not otherwise present on the palette.
 */
export function resolveTone(theme: Theme, severity: StatusSeverity): ToneColors {
  switch (severity) {
    case 'success':
      return theme.palette.success;
    case 'warning':
      return theme.palette.warning;
    case 'error':
      return theme.palette.error;
    case 'info':
      return theme.palette.info;
    case 'accent':
      return theme.palette.secondary;
    case 'indigo':
      // Polaris indigo ramp (kit-data.js TONES.indigo) — the "in ConnectWise"
      // request lifecycle tone; no MUI palette entry, so the documented literal.
      return { main: '#4F46E5', dark: '#3730A3', light: '#818CF8' };
    case 'neutral':
    default:
      // Polaris slate ramp (kit-data.js TONES.neutral). `light` stays bright so
      // it doubles as the dark-mode chip text color.
      return {
        main: theme.palette.text.secondary,
        dark: '#2C3A47',
        light: '#A9B8C4',
      };
  }
}

/**
 * Best-effort map from a domain status code to a tone. The keys are the status
 * strings surfaced by the prototype (case-insensitive); screens that need a
 * different tone pass `severity` to {@link StatusChip} directly rather than
 * relying on this default. These are semantic codes, not display copy — the
 * chip's visible text always comes from the caller-supplied `label`/`status`.
 */
const STATUS_SEVERITY: Record<string, StatusSeverity> = {
  ok: 'success',
  'on track': 'success',
  completed: 'success',
  complete: 'success',
  approved: 'success',
  active: 'success',
  enabled: 'success',
  delivered: 'success',
  warned: 'warning',
  'pending approval': 'warning',
  pending: 'warning',
  'awaiting shipment': 'warning',
  'over budget': 'error',
  denied: 'error',
  failed: 'error',
  rejected: 'error',
  'in progress': 'info',
  'ticket created': 'info',
  'in transit': 'info',
  'return shipment': 'info',
  submitted: 'info',
  'received at depot': 'neutral',
  'partial completion': 'neutral',
  resolved: 'neutral',
  closed: 'neutral',
  draft: 'neutral',
  disconnected: 'neutral',
  inactive: 'neutral',
  'not assessed': 'neutral',
  'repair in progress': 'accent',
};

/** Map a status code to a tone, defaulting to `neutral` for unknown codes. */
export function statusToSeverity(status: string): StatusSeverity {
  return STATUS_SEVERITY[status.trim().toLowerCase()] ?? 'neutral';
}
